import {isServer, useTokenValue} from '@sail/engine';
import {useEffect, useRef, useState} from 'react';
import {tokens} from '@sail/tokens/deprecated';
import {uiRpc} from '../../../lib/ui-rpc/rpc';

type useMatchMediaOptions =
  | {onChange?: (matches: boolean) => void; ssrInitialValue?: boolean}
  | undefined;

export type useMatchMediaReturnType = boolean | null;
type useMatchMedia = (
  query: string,
  options?: useMatchMediaOptions,
) => useMatchMediaReturnType;

export const useRpcMatchMedia: useMatchMedia = (query, options = {}) => {
  const stableOptions = useRef(options);
  stableOptions.current = options;

  // We can not run window.matchMedia on the server.
  // This needs to be initialized with the same value in server and client to avoid hydration mismatches.
  const [matches, setMatches] = useState<useMatchMediaReturnType>(null);

  useEffect(() => {
    const supportsMatchMedia =
      typeof window !== 'undefined' && typeof window?.matchMedia === 'function';

    if (!supportsMatchMedia || isServer || !uiRpc?.client) {
      return;
    }

    const unsubscribe = uiRpc.client.watch(
      'matchMedia',
      {query},
      ({matches}) => {
        setMatches(matches);
        stableOptions.current?.onChange?.(matches);
      },
    );

    return unsubscribe;
  }, [query]);

  return matches;
};

export const useRpcMediaMaxWidth = (
  viewport: string,
  options?: useMatchMediaOptions,
): useMatchMediaReturnType => {
  return useRpcMatchMedia(`(max-width: calc(${viewport} - 0.1px))`, options);
};

export const useRpcMediaAnyCoarsePointer = (
  options?: useMatchMediaOptions,
): useMatchMediaReturnType => {
  return useRpcMatchMedia('(any-pointer: coarse)', options);
};

export const useRpcMediaSmallScreen = (
  options?: useMatchMediaOptions,
): useMatchMediaReturnType => {
  const viewport = useTokenValue(tokens.viewport.small);

  return useRpcMediaMaxWidth(viewport, options);
};
